import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import Logo from "./Logo";

const navLinks = [
  { to: "/home", label: "Menu" },
  { to: "/core", label: "Core" },
  { to: "/campaigns", label: "Campaign" },
  { to: "/legacy", label: "Legacy" },
  { to: "/logbook", label: "Logbook" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // ESC closes the open menu first (capture phase, so page-level ESC
  // handlers see the event as already handled and stay put).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [mobileOpen]);

  return (
    <>
      {/* Tactical top bar */}
      <div
        data-testid="top-banner"
        className="w-full bg-[#11141C] border-b border-[#2A3040] text-[10px] font-mono uppercase tracking-[0.3em] py-1.5 px-4 text-center text-[#A0A6B5] truncate whitespace-nowrap"
      >
        <span className="hud-dot inline-block w-1.5 h-1.5 bg-[#D4AF37] mr-2 align-middle" />
        <span className="sm:hidden">Strength in Order</span>
        <span className="hidden sm:inline">Built through adversity · Forged through leadership · Strength in Order</span>
      </div>

      <header
        data-testid="site-header"
        className="sticky top-0 z-40 w-full bg-[#06080C]/90 backdrop-blur-xl border-b border-[#1F2330]"
      >
        <div className="px-5 md:px-12 h-[68px] sm:h-[76px] md:h-[84px] lg:h-[92px] flex items-center justify-between">
          <Link
            data-testid="logo-home-link"
            to="/home"
            className="flex items-center gap-2 sm:gap-3 lg:gap-4 group"
          >
            <Logo className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16" />
            <div className="leading-none">
              <div className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl tracking-[0.15em] etched-steel">
                AEGIS
              </div>
              <div className="hidden sm:block text-[10px] lg:text-xs font-mono uppercase tracking-[0.3em] text-[#8A91A0] mt-1">
                Strength in Order
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((l) => (
              <NavLink
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                to={l.to}
                className={({ isActive }) =>
                  `text-sm font-mono uppercase tracking-[0.3em] transition-colors ${
                    isActive ? "text-[#D4AF37]" : "text-[#A0A6B5] hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="profile-btn"
              className="hidden md:flex w-12 h-12 border border-[#1F2330] hover:border-[#D4AF37] items-center justify-center transition-colors"
              aria-label="Profile"
              onClick={() => navigate("/legacy")}
            >
              <User className="w-5 h-5 text-[#A0A6B5]" />
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2.5 border border-[#1F2330]"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            id="mobile-menu"
            data-testid="mobile-menu"
            className="lg:hidden border-t border-[#1F2330] bg-[#06080C]"
          >
            <div className="px-5 py-4 flex flex-col gap-3">
              {navLinks.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => setMobileOpen(false)}
                  className="text-left text-base font-mono uppercase tracking-[0.2em] text-[#A0A6B5] hover:text-white py-3 border-b border-[#1F2330]/60"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
